import React, { useContext, useEffect, useMemo, useState } from "react";
import { Table } from "reactstrap";
import ReactPaginate from "react-paginate";
import { format } from "date-fns";
import { ProductContext } from "../../../../store";
import ReportSortArrows from "../../../../components/Search/ReportSortArrows";
import DateRangePickerComponent from "../../../../components/DaterangePicker";
import { Col, Row } from "reactstrap";
import DetailSkeleton from "../../../../components/Skeleton/DetailSkeleton";
import TableSkeleton from "../../../../components/Skeleton/TableSkeleton";
import { PostData } from "../../../../services/FetchData";
import { cmsAssistantSearchReportListUrl } from "../../../../constants/cmsAssistantSearchReportsApi";
import CmsAssistantReportExportButton from "../../../../components/CmsAssistant/CmsAssistantReportExportButton";
import { toast } from "react-toastify";

const COLUMN_DEFS = {
	searchTerms: [
		{ key: "query", label: "Query", sort: "query" },
		{ key: "count", label: "Search volume", sort: "count", align: "end" },
		{ key: "zeroResultPct", label: "Zero-result %", sort: "zeroResultPct", align: "end" },
	],
	zeroKeywordResults: [
		{ key: "query", label: "Query", sort: "query" },
		{ key: "count", label: "Zero count", sort: "count", align: "end" },
		{ key: "withAiOverviewCount", label: "With AI overview", sort: "withAiOverviewCount", align: "end" },
		{ key: "hybridOnlyZeroCount", label: "Hybrid-only zero", sort: "hybridOnlyZeroCount", align: "end" },
	],
	zeroSessionsNoAi: [
		{ key: "query", label: "Query", sort: "query" },
		{ key: "hybridOnlyZeroCount", label: "Empty session count", sort: "count", align: "end" },
	],
	aiOverviews: [
		{ key: "query", label: "Query", sort: "query" },
		{ key: "count", label: "Count", sort: "count", align: "end" },
		{ key: "avgCitationCount", label: "Avg citations", sort: "avgCitationCount", align: "end" },
	],
	resultsClicked: [
		{ key: "title", label: "Title", sort: "title" },
		{ key: "url", label: "URL", sort: null },
		{ key: "count", label: "Clicks", sort: "count", align: "end" },
		{ key: "avgRank", label: "Avg rank", sort: "avgRank", align: "end" },
	],
	aiCitationsClicked: [
		{ key: "title", label: "Title", sort: "title" },
		{ key: "url", label: "URL", sort: null },
		{ key: "count", label: "Clicks", sort: "count", align: "end" },
		{ key: "avgRank", label: "Avg rank", sort: "avgRank", align: "end" },
	],
	topAiCitations: [
		{ key: "title", label: "Citation title", sort: "title" },
		{ key: "count", label: "Impressions", sort: "count", align: "end" },
	],
};

const EnterpriseSearchReportPage = ({ reportKey, title, description }) => {
	const [pageLoader, setPageLoader] = useState(true);
	const [tableLoader, setTableLoader] = useState(true);
	const [rows, setRows] = useState([]);
	const [totalPageCount, setTotalPageCount] = useState(0);
	const [limit] = useState(25);
	const [page, setPage] = useState(0);
	const [sortField, setSortField] = useState("count");
	const [sortOrder, setSortOrder] = useState("desc");
	const { dateRange, setDateRange, currentWebsiteId } = useContext(ProductContext);

	const columns = useMemo(
		() => COLUMN_DEFS[reportKey] || [{ key: "query", label: "Data", sort: "query" }],
		[reportKey]
	);

	useEffect(() => {
		if (currentWebsiteId) getReports();
	}, [dateRange, limit, page, currentWebsiteId, sortField, sortOrder, reportKey]);

	const toggleSort = (field, e) => {
		e?.stopPropagation?.();
		if (!field) return;
		setPage(0);
		if (sortField === field) {
			setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
		} else {
			setSortField(field);
			setSortOrder("desc");
		}
	};

	const getReports = () => {
		setTableLoader(true);
		const payload = {
			websiteId: currentWebsiteId,
			reportKey,
			startDate: format(dateRange.startDate, "yyyy-MM-dd"),
			endDate: format(dateRange.endDate, "yyyy-MM-dd"),
			page: page + 1,
			limit,
			sortField: sortField || "count",
			sortOrder: sortOrder || "desc",
			resultsPageOnly: reportKey === "searchTerms",
		};

		PostData("ms4", cmsAssistantSearchReportListUrl(), payload)
			.then((response) => {
				if (!response || response.status !== "success" || !response.data) {
					toast.error(response?.message || "Failed to fetch report");
					setRows([]);
					return;
				}
				const dataArray = Array.isArray(response.data.data) ? response.data.data : [];
				setRows(dataArray);
				const totalRecord = response.data.total || 0;
				setTotalPageCount(Math.ceil(totalRecord / limit) || 0);
			})
			.catch((err) => {
				console.error("Enterprise search report error:", err);
				setRows([]);
			})
			.finally(() => {
				setTableLoader(false);
				setPageLoader(false);
			});
	};

	if (pageLoader) {
		return <DetailSkeleton />;
	}

	return (
		<div className="pageContent">
			<Row className="mt-3 align-items-center justify-content-between">
				<Col xs="12" md="6">
					<h2>{title}</h2>
					{description ? <p className="text-muted small mb-0">{description}</p> : null}
				</Col>
				<Col xs="12" md="auto" className="d-flex gap-2 align-items-center flex-wrap">
					<DateRangePickerComponent dateRange={dateRange} onSelect={(range) => setDateRange(range)} />
					<CmsAssistantReportExportButton
						reportKey={reportKey}
						sortField={sortField}
						sortOrder={sortOrder}
						resultsPageOnly={reportKey === "searchTerms"}
					/>
				</Col>
			</Row>
			{tableLoader ? (
				<TableSkeleton rows={8} cols={columns.length} />
			) : (
				<>
					<div className="table-responsive mt-3">
						<Table bordered hover size="sm">
							<thead className="table-light">
								<tr>
									{columns.map((col) => (
										<th
											key={col.key}
											className={col.align === "end" ? "text-end" : undefined}
											style={col.sort ? { cursor: "pointer" } : undefined}
											onClick={col.sort ? (e) => toggleSort(col.sort, e) : undefined}
										>
											{col.label}
											{col.sort ? (
												<ReportSortArrows
													field={col.sort}
													activeSortField={sortField}
													sortOrder={sortOrder}
													onSort={(field, order, e) => {
														e?.stopPropagation?.();
														setPage(0);
														setSortField(field);
														setSortOrder(order);
													}}
												/>
											) : null}
										</th>
									))}
								</tr>
							</thead>
							<tbody>
								{rows.length ? (
									rows.map((row, i) => (
										<tr key={`${reportKey}-row-${i}`}>
											{columns.map((col) => (
												<td
													key={col.key}
													className={col.align === "end" ? "text-end" : undefined}
												>
													{row[col.key] ?? "—"}
												</td>
											))}
										</tr>
									))
								) : (
									<tr>
										<td colSpan={columns.length} className="text-center text-muted">
											No data for this date range
										</td>
									</tr>
								)}
							</tbody>
						</Table>
					</div>
					{totalPageCount > 1 ? (
						<ReactPaginate
							previousLabel="Previous"
							nextLabel="Next"
							breakLabel="..."
							pageCount={totalPageCount}
							marginPagesDisplayed={2}
							pageRangeDisplayed={5}
							onPageChange={(e) => setPage(e.selected)}
							containerClassName="pagination"
							activeClassName="active"
							forcePage={page}
						/>
					) : null}
				</>
			)}
		</div>
	);
};

export default EnterpriseSearchReportPage;
